/**
 * Asks the "Search or ask" resolver (CLA-1805) about what is being typed in
 * the ⌘K palette (CLA-1813).
 *
 * Timing, per the contract on CLA-1795:
 * - `final: false` once typing pauses for 250 ms, so keystroke answers stay on
 *   the fast embedding path;
 * - `final: true` after a 700 ms pause, or at once on `submitFinal()` (Enter).
 *   Only a final query may reach the server's language-model fallback.
 * - New input cancels whatever is in flight and any timer still waiting.
 *
 * Nothing is sent while `enabled` is false (`assist_available` is not true) or
 * the query is blank.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { resolveAssist } from '@/api/search'
import type { AssistResolveResponse } from '@/api/types'

export const ASSIST_DEBOUNCE_MS = 250
export const ASSIST_FINAL_PAUSE_MS = 700

export interface AssistAnswer {
  /** The (trimmed) query this answers. */
  query: string
  /** Whether it answered a final query. */
  final: boolean
  response: AssistResolveResponse
}

export interface AssistContext {
  patientRef?: string | null
  noteRef?: string | null
}

export interface AssistResolveState {
  /** The latest answer. It may be for an earlier query while the next is on its way. */
  answer: AssistAnswer | null
  /** A final answer for the current query has not arrived (and has not failed). */
  loading: boolean
  /** Send the current query as final now (Enter), skipping the pause. */
  submitFinal: () => void
  /** Forget the last answer (the palette was reopened). */
  reset: () => void
}

export function useAssistResolve(
  enabled: boolean,
  rawQuery: string,
  context: AssistContext = {},
): AssistResolveState {
  const query = rawQuery.trim()
  const patientRef = context.patientRef ?? null
  const noteRef = context.noteRef ?? null

  const [answer, setAnswer] = useState<AssistAnswer | null>(null)
  const [failedQuery, setFailedQuery] = useState<string | null>(null)

  const controllerRef = useRef<AbortController | null>(null)
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([])
  // The query a final request has already gone out for, so Enter after the
  // pause (or twice) does not ask again.
  const finalSentRef = useRef<string | null>(null)

  const clearTimers = useCallback(() => {
    for (const t of timersRef.current) clearTimeout(t)
    timersRef.current = []
  }, [])

  const send = useCallback(
    (q: string, final: boolean) => {
      controllerRef.current?.abort()
      const controller = new AbortController()
      controllerRef.current = controller
      if (final) finalSentRef.current = q
      resolveAssist(
        {
          query: q,
          final,
          context: { screen: 'command_palette', patient_ref: patientRef, note_ref: noteRef },
        },
        { signal: controller.signal },
      )
        .then((response) => {
          if (!controller.signal.aborted) setAnswer({ query: q, final, response })
        })
        .catch(() => {
          // A failed keystroke query is forgotten: the final one follows.
          if (!controller.signal.aborted && final) setFailedQuery(q)
        })
    },
    [patientRef, noteRef],
  )

  useEffect(() => {
    finalSentRef.current = null
    if (!enabled || !query) return
    timersRef.current = [
      setTimeout(() => send(query, false), ASSIST_DEBOUNCE_MS),
      setTimeout(() => send(query, true), ASSIST_FINAL_PAUSE_MS),
    ]
    return () => {
      clearTimers()
      controllerRef.current?.abort()
      controllerRef.current = null
    }
  }, [enabled, query, send, clearTimers])

  const submitFinal = useCallback(() => {
    if (!enabled || !query || finalSentRef.current === query) return
    clearTimers()
    send(query, true)
  }, [enabled, query, send, clearTimers])

  const reset = useCallback(() => {
    clearTimers()
    controllerRef.current?.abort()
    controllerRef.current = null
    finalSentRef.current = null
    setAnswer(null)
    setFailedQuery(null)
  }, [clearTimers])

  const settled = answer !== null && answer.query === query && answer.final
  const loading = enabled && query !== '' && !settled && failedQuery !== query

  return { answer, loading, submitFinal, reset }
}
